import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, Search, ArrowUp, ArrowDown } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import ProjectFormSlider from '../components/ProjectFormSlider';
import { Project } from '../types/project';
import api from '../services/api';
import '../styles/Projects.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy] = useState<'name'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Get drawer state from URL params
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editProjectId = searchParams.get('edit');

  useEffect(() => {
    if (workspaceId) {
      // Initial load or search query change - full loading
      if (projects.length === 0 || searchQuery !== '') {
        fetchProjects(false);
      } else {
        // Data refresh (sort, pagination) - only tbody loading
        fetchProjects(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, searchQuery, sortBy, sortOrder]);

  const fetchProjects = async (isDataRefresh: boolean = false) => {
    try {
      // Use dataLoading for data refreshes (sort, pagination), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          sortBy: sortBy,
          sortOrder: sortOrder
        }
      });

      setProjects(response.data.projects);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleCreateProject = () => {
    setSearchParams({ action: 'new' });
  };

  const handleProjectClick = (project: Project) => {
    navigate(`/workspaces/${workspaceId}/projects/${project._id}`);
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
  };

  const handleFormSuccess = () => {
    setSearchParams({});
    fetchProjects();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getSortIcon = () => {
    return sortOrder === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
  };

  const getProjectTypeDisplay = (project: Project): string => {
    if (!project.additionalInfo || project.additionalInfo.length === 0) {
      return '—';
    }

    const info = project.additionalInfo[0];
    const type = info.type.charAt(0).toUpperCase() + info.type.slice(1).replace('-', ' ');

    const details: string[] = [];
    if (info.totalUnits) details.push(`${info.totalUnits} Units`);
    if (info.totalFloors) details.push(`${info.totalFloors} Floors`);
    if (info.totalBuildings) details.push(`${info.totalBuildings} Buildings`);
    if (info.totalHomes) details.push(`${info.totalHomes} Homes`);

    if (details.length === 0) return type;
    return `${type} • ${details.join(' • ')}`;
  };

  const getProjectInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="projects-container">
        {/* Section Header */}
        <div className="projects-section-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 className="projects-section-title">Projects</h2>
              <p className="projects-section-subtitle">
                Manage residential development projects
              </p>
            </div>
            <button className="btn btn-primary btn-md" onClick={handleCreateProject}>
              + New Project
            </button>
          </div>
        </div>

        {/* Card Container for Search and Table */}
        <div className="projects-card-container">
          {/* Actions Bar */}
          <div className="projects-actions">
            <div className="projects-search">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="form-error" style={{ marginBottom: '24px' }}>
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="projects-loading">
              <div className="projects-loading-spinner" />
            </div>
          )}

          {/* Empty State */}
          {!loading && projects.length === 0 && !searchQuery && (
            <div className="projects-empty">
              <div className="projects-empty-icon">
                <Building2 size={48} color="#6B7280" />
              </div>
              <h3 className="projects-empty-title">No projects yet</h3>
              <p className="projects-empty-text">
                Get started by adding your first project
              </p>
              <button className="btn btn-primary btn-lg" onClick={handleCreateProject}>
                Create your first project
              </button>
            </div>
          )}

          {/* No Search Results */}
          {!loading && projects.length === 0 && searchQuery && (
            <div className="projects-empty">
              <div className="projects-empty-icon">
                <Search size={48} color="#6B7280" />
              </div>
              <h3 className="projects-empty-title">No projects found</h3>
              <p className="projects-empty-text">
                Try adjusting your search criteria
              </p>
            </div>
          )}

          {/* Projects Table */}
          {!loading && projects.length > 0 && (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Logo</th>
                    <th>
                      <button
                        onClick={handleSort}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Project Name
                        <span className="sort-icon">
                          {getSortIcon()}
                        </span>
                      </button>
                    </th>
                    <th>Builder</th>
                    <th>Type & Details</th>
                    <th>Status</th>
                    <th>Contact</th>
                  </tr>
                </thead>
                <tbody className={dataLoading ? 'loading' : ''}>
                  {projects.map((project, index) => (
                    <tr
                      key={project._id}
                      onClick={() => handleProjectClick(project)}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                      }}
                    >
                      <td>
                        {project.logoUrl ? (
                          <div style={{
                            width: '48px',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <img
                              src={project.logoUrl}
                              alt={project.name}
                              style={{
                                maxWidth: '48px',
                                maxHeight: '48px',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        ) : (
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#F3F4F6',
                            border: '1px solid #E5E7EB'
                          }}>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#6B7280',
                              textTransform: 'uppercase'
                            }}>
                              {getProjectInitials(project.name)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="data-table-primary-cell">
                        {project.name}
                        {project.projectCode && (
                          <span className="data-table-secondary-cell" style={{ display: 'block', marginTop: '2px' }}>
                            {project.projectCode}
                          </span>
                        )}
                      </td>
                      <td>{project.builderName || '—'}</td>
                      <td className="data-table-secondary-cell">
                        {getProjectTypeDisplay(project)}
                      </td>
                      <td>
                        <span className={`data-table-status data-table-status-${project.status?.toLowerCase().replace(/\s+/g, '-') || 'pending'}`}>
                          {project.status || 'N/A'}
                        </span>
                      </td>
                      <td>{project.contactEmail || project.contactWebsite || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="projects-pagination">
                <button
                  className="projects-pagination-btn"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  ← Prev
                </button>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`projects-pagination-page ${
                      page === pagination.page ? 'active' : ''
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="projects-pagination-btn"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
          )}
        </div>
      </div>

      {/* Project Form Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title={editProjectId ? 'Edit Project' : 'Create New Project'}
        footer={
          <div className="drawer-footer-actions">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={handleCloseDrawer}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              form="project-form"
            >
              {editProjectId ? 'Update Project' : 'Create Project'}
            </button>
          </div>
        }
      >
        {workspaceId && (
          <ProjectFormSlider
            workspaceId={workspaceId}
            projectId={editProjectId || undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseDrawer}
          />
        )}
      </Drawer>
    </AppLayout>
  );
};

export default Projects;
